import { useEffect, useState } from 'react'
import type { Installation } from '@/features/suppliers/hooks/installations'
import type { SupplierEmissionsReport } from '@/features/suppliers/hooks/supplierEmissionsReport'
import {
  getInstallationEligibility,
  getEmissionsReportEligibility,
  type GetAggregatedCategoryForCnCode,
  type InstallationIneligibilityReason,
  type SelectedGoodCountryCn
} from '@/shared/utils/batchUpdateSelectionRules'
import { EMPTY_VALUE } from '../pages/cbam-report-details/components/bulkUpdateSupplierEmissionsForm/BulkUpdateSupplierEmissionsForm.schema'

type InstallationEligibility = {
  eligibleInstallationIds: string[]
  ineligibilityReason: InstallationIneligibilityReason | null
}

type EmissionsEligibility = {
  canSelect: boolean
  tooltipKey: string | null
  eligibleReportIds: string[]
}

type UseSupplierEmissionsEligibilityParams = {
  selectedGoods: SelectedGoodCountryCn[]
  installations: Installation[]
  emissionsReports: SupplierEmissionsReport[]
  getAggregatedCategoryForCnCode: GetAggregatedCategoryForCnCode
  /** Current installation id; treat EMPTY_VALUE as no selection. */
  selectedInstallationId: string | null
  cbamReportYear?: number
}

export function useSupplierEmissionsEligibility({
  selectedGoods,
  installations,
  emissionsReports,
  getAggregatedCategoryForCnCode,
  selectedInstallationId,
  cbamReportYear
}: UseSupplierEmissionsEligibilityParams): {
  installationEligibility: InstallationEligibility
  emissionsEligibility: EmissionsEligibility
} {
  const [installationEligibility, setInstallationEligibility] = useState<InstallationEligibility>({
    eligibleInstallationIds: [],
    ineligibilityReason: null
  })
  const [emissionsEligibility, setEmissionsEligibility] = useState<EmissionsEligibility>({
    canSelect: false,
    tooltipKey: null,
    eligibleReportIds: []
  })

  const normalizedInstallationId =
    selectedInstallationId && selectedInstallationId !== EMPTY_VALUE ? selectedInstallationId : null

  useEffect(() => {
    const installationsForEligibility = installations.map(inst => ({
      id: inst.id,
      country: inst.country,
      goodsFamily: inst.goodsFamily,
      partnershipId: inst.partnershipId
    }))
    if (selectedGoods.length === 0) {
      setInstallationEligibility({
        eligibleInstallationIds: installations.map(i => i.id),
        ineligibilityReason: null
      })
      return
    }
    const result = getInstallationEligibility({
      selectedGoods,
      installations: installationsForEligibility,
      getAggregatedCategoryForCnCode
    })
    setInstallationEligibility({
      eligibleInstallationIds: result.eligibleInstallationIds,
      ineligibilityReason: result.ineligibilityReason
    })
  }, [selectedGoods, installations, getAggregatedCategoryForCnCode])

  useEffect(() => {
    const result = getEmissionsReportEligibility({
      selectedGoods: selectedGoods.map(good => ({ cnCode: good.cnCode })),
      selectedInstallationId: normalizedInstallationId,
      emissionsReports: emissionsReports.map(report => ({
        id: report.id,
        installationId: report.installationId,
        periodId: report.periodId,
        goods: report.goods.map(good => ({ cnCode: good.cnCode }))
      })),
      cbamReportYear
    })
    setEmissionsEligibility({
      canSelect: result.canSelect,
      tooltipKey: result.tooltipKey,
      eligibleReportIds: result.eligibleReportIds
    })
  }, [selectedGoods, normalizedInstallationId, emissionsReports, cbamReportYear])

  return { installationEligibility, emissionsEligibility }
}
